import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AlertController, Events } from "ionic-angular";
import { chain, isArray, isEmpty, map } from "lodash";
import moment from "moment";
import { Observable } from "rxjs/Observable";
import { ConfigurationService } from "../configuration/configuration.service";
import { MobileUtilsProvider } from "../mobile-utils/mobile-utils";
import { OfflineAsmtDataServiceProvider } from "../offline-asmt-data-service/offline-asmt-data-service";
import { DatabaseProvider } from "../offline-database/db-connection";
import { DbQueries } from "../offline-database/db-queries";
import {
  isNewOfflineVisit,
  isUnplannedPatient,
  prepareOfflineActionToSync,
  SQLiteProgress,
  stringifyJSON,
} from "../offline-manager/offline-globals";
import { PatientDataManager } from "../offline-manager/patient-data-manager";

/*
  Generated class for the MobileOfflineDataProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class MobileOfflineDataProvider {
  apiUrl: string;
  spsUrl: string;
  constructor(
    public http: HttpClient,
    private mobileUtils: MobileUtilsProvider,
    public events: Events,
    public database: DatabaseProvider,
    private offlineAsmtData: OfflineAsmtDataServiceProvider,
    private patientDataManager: PatientDataManager,
    private alertCtrl: AlertController,
    private config: ConfigurationService
  ) {
    this.apiUrl = config.getConfig().apiUrl;
    if (this.config.getConfig().spsUrl) {
      this.spsUrl = this.config.getConfig().spsUrl;
    } else {
      this.spsUrl = this.config.getConfig().apiUrl;
    }
  }

  syncDataService(syncData) {
    const url = `${this.apiUrl}/assessmentOffline/assessmentSync/`;
    return new Observable((observer) => {
      this.http.put(url, syncData).subscribe(
        (data: any) => {
          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          let alert = this.alertCtrl.create({
            title: "Sync Error",
            buttons: ["Dismiss"],
          });
          alert.present();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  public getOfflineActionsCount(): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          let totalActions = 0;
          db.transaction(
            (tx) => {
              tx.executeSql(
                DbQueries.OFFLINE_ACTION_SELECT_ALL_COUNT,
                [],
                (tx, resultSet) => {
                  totalActions = resultSet.rows.item(0).totalActions;
                }
              );
            },
            (error) => {
              console.log(
                `Transaction ERROR (getOfflineActionsCount): ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (getOfflineActionsCount)`);
              observer.next(totalActions);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(
            `getOfflineActionsCount.getInstance error: ${error.message}`
          );
          observer.error(error);
          observer.complete();
        }
      );
    });
  }

  public getOfflineActions(): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          const actions = [];
          db.transaction(
            (tx) => {
              tx.executeSql(
                DbQueries.OFFLINE_ACTION_SELECT_ALL,
                [],
                (tx, resultSet) => {
                  const expiredActions = [];
                  const today = moment().startOf("day").valueOf();
                  for (let i = 0; i < resultSet.rows.length; i++) {
                    const action = resultSet.rows.item(i);
                    if (today > action.expire_at) {
                      expiredActions.push(action);
                    } else {
                      actions.push(action);
                    }
                  }

                  // Remove expired actions
                  if (!isEmpty(expiredActions)) {
                    this.removeExpireOfflineActions(expiredActions);
                  }
                },
                (tx, error) => {
                  console.log(
                    `SELECT error (getOfflineActions): ${error.message}`
                  );
                }
              );
            },
            (error) => {
              console.log(
                `Transaction ERROR (getOfflineActions): ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (getOfflineActions)`);
              observer.next(actions);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(`getOfflineActions.getInstance error: ${error.message}`);
          observer.error(error);
          observer.complete();
        }
      );
    });
  }

  public removeExpireOfflineActions(expiredActions: any) {
    const ids = [];
    const backupPayload = [];
    expiredActions.forEach((action) => {
      ids.push(action.action_id);
      backupPayload.push(prepareOfflineActionToSync(action));
    });

    // Delete from local DB
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            tx.executeSql(
              `DELETE FROM offline_actions WHERE action_id IN (${ids.toString()})`,
              []
            );
          },
          (error) => {
            console.log(
              `Transaction ERROR (removeExpireOfflineActions): ${error.message}`
            );
          },
          () => {
            console.log(`Transaction ok (removeExpireOfflineActions)`);
          }
        );
      },
      (error) => {
        console.log(
          `removeExpireOfflineActions.getInstance error: ${error.message}`
        );
      }
    );

    // Backup on server
    this.http
      .put(
        `${this.apiUrl}/assessmentOffline/assessmentSyncAudit/`,
        backupPayload
      )
      .subscribe();
  }

  public removeOfflineAction(offlineActionData: any) {
    offlineActionData.forEach((offlineAction) => {
      const { syncKeepRetrying, conflict, syncStatus, actionId } =
        offlineAction;
      const serverAssessmentId = offlineAction.assessmentId;

      if (conflict === 0 && !syncKeepRetrying) {
        this.getOfflineActionsByActionId(actionId).subscribe(
          (offlineActionData) => {
            const { patient_id, patient_name } = offlineActionData;
            const localAssessmentId = offlineActionData.assessment_id;

            if (isNewOfflineVisit(localAssessmentId)) {
              if (isEmpty(serverAssessmentId) && syncStatus === "FAILED") {
                this.deleteLocalChanges(
                  localAssessmentId,
                  isUnplannedPatient(patient_name) ? patient_id : null
                ).subscribe();
              } else if (serverAssessmentId && syncStatus === "SUCCESS") {
                this.updateLocalChanges(
                  localAssessmentId,
                  serverAssessmentId
                ).subscribe();
              }
            }
            this.offlineAsmtData
              .deleteOfflineActions(offlineAction)
              .subscribe();
          }
        );
      }
    });
  }

  public deleteLocalChanges(assessmentId, patientId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(DbQueries.DELETE_ASSESSMENT_DETAILS_BY_ASMT_ID, [
                assessmentId,
              ]);
              tx.executeSql(DbQueries.DELETE_ASSESSMENT_TEMPLATE_BY_ASMT_ID, [
                assessmentId,
              ]);
              tx.executeSql(DbQueries.DELETE_ASSESSMENT_BY_ASMT_ID, [
                assessmentId,
              ]);
              if (patientId) {
                tx.executeSql(DbQueries.DELETE_PATIENT_BY_CHART_ID, [
                  patientId,
                ]);
              }
            },
            (error) => {
              console.log(
                `Transaction ERROR (deleteLocalChanges): ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (deleteLocalChanges)`);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(`deleteLocalChanges.getInstance error: ${error.message}`);
          observer.complete();
        }
      );
    });
  }

  public updateLocalChanges(
    offlineActionAsmtId,
    assessmentId
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe(
        (db) => {
          db.transaction(
            (tx) => {
              tx.executeSql(DbQueries.UPDATE_ASSESSMENT_DETAILS_BY_ASMT_ID, [
                assessmentId,
                offlineActionAsmtId,
              ]);
              tx.executeSql(DbQueries.ASSESSMENT_TEMPLATE_UPDATE_BY_ASMT_ID, [
                assessmentId,
                offlineActionAsmtId,
              ]);
              tx.executeSql(DbQueries.ASSESSMENT_UPDATE_BY_ID, [
                assessmentId,
                offlineActionAsmtId,
              ]);
            },
            (error) => {
              console.log(
                `Transaction ERROR (updateLocalChanges): ${error.message}`
              );
              observer.error(error);
              observer.complete();
            },
            () => {
              console.log(`Transaction ok (updateLocalChanges)`);
              observer.complete();
            }
          );
        },
        (error) => {
          console.log(`updateLocalChanges.getInstance error: ${error.message}`);
          observer.complete();
        }
      );
    });
  }

  public getOfflineActionsByActionId(actionId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.OFFLINE_ACTION_SELECT_BY_ACTION_ID,
            [actionId],
            (_, resultSet) => {
              let offlineActionData: any = {};
              if (resultSet.rows.length > 0) {
                offlineActionData = resultSet.rows.item(0);
              }
              observer.next(offlineActionData);
            },
            (_, error) => {
              console.log(
                `GET OFFLINE ACTION DATA BY ID error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  downloadPreparedModeData(
    userId: number,
    calendarId: string,
    timeZone: string,
    forceDownload: boolean = true
  ) {
    let today = moment().format("YYYY-MM-DD");
    if (!forceDownload) {
      // On current day, download offline patients on login Or first app launch
      let lastDownloadedAt = localStorage.getItem(
        this.mobileUtils.mobileDataDownloadedAtKey
      );
      if (lastDownloadedAt === today) {
        console.log(`Mobile data is already downloaded for today ${today}`);
        return;
      }
    }

    //downloadPreparedModeData
    console.log(`Mobile data download is stated from ${today}`);
    localStorage.setItem(this.mobileUtils.mobileDataDownloadedAtKey, today);
    const url = `${this.spsUrl}/assessmentOffline/master/calendar?timeZone=${timeZone}&startDate=${today}&endDate=${today}&calendarId=${calendarId}`;
    this.events.publish("onSQLiteProgress", SQLiteProgress.Started);
    this.http.get(url).subscribe(
      (data) => {
        this.dumpAllAssessmentsData(data);
        this.dumpAllPatientsData(data, userId);
        this.dumpAssessmentDetails(data);
        this.dumpAssessmentTemplates(data);
        this.dumpAsmtValidation(data);
        this.dumpAllTemplateReplacementData(data);
      },
      (error) => {
        this.events.publish("onSQLiteProgress", SQLiteProgress.Finished);
      }
    );
  }

  dumpAllAssessmentsData(data) {
    let assessments = [];
    //Preapare data to insert in SQLite
    for (var patientId in data) {
      let visit = data[patientId].visitList;
      let visits = visit.list ? visit.list : [];
      let asmtMap = data[patientId].assessmentGeneratedMap;
      for (var i = 0; i < visits.length; i++) {
        let asmtExtraDetails = asmtMap[visits[i].assessmentId].extraDetails;
        assessments.push(
          this.prepareAsmtDBObject(visits[i], patientId, asmtExtraDetails)
        );
      }
    }
    console.log(
      `dumpAllAssessment started at ${new Date()}, Total records: ${
        assessments.length
      }`
    );
    this.offlineAsmtData.dumpAllAssessments(assessments);
  }

  dumpAllPatientsData(data, userId) {
    let patientArr = [];
    //Preapare data to insert in SQLite
    for (var patientId in data) {
      let patientObj = data[patientId].patientProfile;
      patientObj["userId"] = userId;
      patientObj["dareStatus"] = data[patientId].dareStatus;
      patientArr.push(patientObj);
    }
    this.patientDataManager.dumpAllPatients(patientArr);
  }

  dumpAllTemplateReplacementData(data) {
    const replacements = [];
    //Preapare data to insert in SQLite
    for (let patientId in data) {
      const replacement = data[patientId].templateReplacementMap;
      replacement.patientId = Number(patientId);
      replacements.push(replacement);
    }
    this.patientDataManager.dumpAllTemplateReplacementData(replacements);
  }

  prepareAsmtDBObject(response, patientId, dateResponse) {
    const asmt = {
      pat_chart_id: patientId,
      _id: response.assessmentId,
      _uuid: response.assessmentUUID,
      role_lookup_code: response.roleLookUpCode,
      role_lookup_value: response.roleLookUpValue,
      asmt_type_lookup_code: response.asmtTypeLookUpCode,
      asmt_type_lookup_value: response.asmtTypeLookUpValue,
      performed_by: response.performedBy,
      performed_by_id: response.performedById,
      started_dte: response.startedDte,
      end_dte: response.endDte,
      duration: response.duration,
      asmt_status_lookup_code: response.asmtStatusLookUpCode,
      asmt_status_lookup_value: response.asmtStatusLookUpValue,
      care_plan_status_lookup_code: response.carePlanStatusLookUpCode,
      care_plan_status_lookup_value: response.carePlanStatusLookUpValue,
      addendum: response.addendum,
      coordinationOfCareAddendum: response.coordinationOfCareAddendum,
      timezone: response.timezone,
      created_date: dateResponse.createdDate,
      completed_date: dateResponse.completedDate,
    };
    return asmt;
  }

  loadInitialVisitDetailsPageData() {
    // TODO -- BBP I don't think this is needed. Empty subscribe
    // const visitSectionLookupURL = `${this.apiUrl}/users/permissions/assessment_section`;
    // this.http.get(visitSectionLookupURL).subscribe();
  }

  dumpAssessmentTemplates(data: any) {
    if (isEmpty(data)) {
      return;
    }

    let clearPrevious = true;
    try {
      chain(data)
        .keys(data)
        .value()
        .map((patientId: any) => {
          if (patientId) {
            const asmtMap = data[patientId].assessmentGeneratedMap;
            this.prepareAssessmentTemplates(patientId, asmtMap, clearPrevious);
            clearPrevious = false;
          }
        });
    } catch (error) {
      console.log("Func dumpAssessmentTemplates error", error);
    }
  }

  prepareAssessmentTemplates(patientId, asmtMap, clearPrevious) {
    if (isEmpty(asmtMap)) {
      return;
    }

    let templates = [];
    for (var asmtId in asmtMap) {
      let asmt = asmtMap[asmtId].assessmentGenerated;
      let asmtTemplates = asmt["assessmentTemplates"];
      if (isArray(asmtTemplates)) {
        asmtTemplates.forEach((sectionTpl) => {
          templates.push(
            this.prepareAssessmentTemplateDBObj(patientId, sectionTpl)
          );
        });
      }
    }
    console.log(
      `dumpAllAssessmentTemplates started at ${new Date()}, Patient: ${patientId}, Total records: ${
        templates.length
      }`
    );
    this.offlineAsmtData.dumpAllAssessmentTemplates(
      patientId,
      templates,
      clearPrevious
    );
  }

  prepareAssessmentTemplateDBObj(patientId: number, templateObj: any) {
    let tmpStr;
    try {
      tmpStr = JSON.stringify(templateObj.tmplJson);
    } catch (e) {
      tmpStr = null;
    }

    return {
      id: templateObj.id,
      uuid: templateObj.uuid,
      tenant_id: templateObj.tenantId,
      pat_chart_id: patientId,
      assessment_id: templateObj.assessmentId,
      rec_type_lookup_code: templateObj.recTypeLookupCode,
      tmpl_json: tmpStr,
      created_by: templateObj.createdBy,
      created_date: templateObj.createdDte,
      updated_by: templateObj.updatedBy,
      updated_date: templateObj.updatedDte,
      version: templateObj.version,
      active_flag: templateObj.activeFlg,
    };
  }

  dumpAssessmentDetails(data: any) {
    if (isEmpty(data)) {
      return;
    }
    this.offlineAsmtData.clearAsmtDetails().subscribe(() => {
      try {
        chain(data)
          .keys(data)
          .value()
          .map((patientId: any) => {
            if (patientId) {
              const asmtMap = data[patientId].assessmentGeneratedMap;
              this.prepareAssessmentMap(patientId, asmtMap);
            }
          });
      } catch (error) {
        console.log("Func dumpAssessmentDetails error", error);
      }
    });
  }

  prepareAssessmentMap(patientId, asmtMap) {
    if (isEmpty(asmtMap)) {
      return;
    }
    try {
      chain(asmtMap)
        .keys(asmtMap)
        .value()
        .map((asmtId: any) => {
          if (asmtId) {
            const asmt = asmtMap[asmtId].assessmentGenerated;
            this.processAssessmentDetails(patientId, asmt);
          }
        });
    } catch (error) {
      console.log("Func prepareAssessmentMap error", error);
    }
  }

  processAssessmentDetails(patientId, asmt) {
    try {
      this.insertAssessmentDetails(patientId, asmt.assessmentDetails);
    } catch (error) {
      console.log("Func processAssessmentDetails error", error);
    }
  }

  insertAssessmentDetails(patientId, asmtDetails) {
    if (!isArray(asmtDetails)) {
      return;
    }
    const asmtDetailsValue = this.prepareAsmtDetailsValue(
      patientId,
      asmtDetails
    );
    this.offlineAsmtData.insertAssessmentDetails(asmtDetailsValue);
  }

  prepareAsmtDetailsValue(patientId, asmtDetails) {
    try {
      const asmtDetailsValues = [];
      map(asmtDetails, (asmt) => {
        if (
          asmt.childAssessmentDetails &&
          !isEmpty(asmt.childAssessmentDetails)
        ) {
          map(asmt.childAssessmentDetails, (childAsmt) => {
            asmtDetailsValues.push(
              this.prepareAsmtDbValues(patientId, childAsmt)
            );
          });
        }
        asmtDetailsValues.push(this.prepareAsmtDbValues(patientId, asmt));
      });
      return asmtDetailsValues;
    } catch (error) {
      console.log("prepareAsmtDetailsValue mapping error ", error);
    }
  }

  prepareAsmtDbValues(patientId, asmt) {
    return {
      id: asmt.id,
      uuid: asmt.uuid,
      tenant_id: asmt.tenantId,
      assessment_id: asmt.assessmentId,
      pat_chart_id: patientId,
      assessment_detail_id: asmt.assessmentDetailId || 0,
      status_lookup_code: asmt.statusLookupCode,
      section_type_lookup_code: asmt.sectionTypeLookupCode,
      rec_type_lookup_code: asmt.recTypeLookupCode || null,
      data_json: asmt.dataJson || null,
      level: asmt.level,
      created_by: asmt.createdBy,
      created_date: asmt.createdDte,
      updated_by: asmt.updatedBy,
      updated_date: asmt.updatedDte,
      version: asmt.version,
      active_flag: asmt.activeFlg,
    };
  }

  dumpAsmtValidation(data) {
    let validation = [];
    for (let patientId in data) {
      let asmtValidation = data[patientId].assessmentValidationData;
      validation.push(this.prepareAsmtValidation(asmtValidation, patientId));
    }
    this.offlineAsmtData.insertAsmtValidation(validation);
  }

  prepareAsmtValidation(asmtValidation, patientId) {
    const {
      admitVisitCount,
      icaVisitMap,
      previousAssessmentCodeMap,
      openAssessmentCount,
      patientDefaultTimezone,
    } = asmtValidation;
    return {
      patientId: patientId,
      admitVisit: admitVisitCount,
      icaVisit: stringifyJSON(icaVisitMap),
      previouseAsmt: stringifyJSON(previousAssessmentCodeMap),
      openAsmt: openAssessmentCount,
      patientTimezone: patientDefaultTimezone,
    };
  }

  loadInitialVisitPageData() {
    // TODO - BBP Don't think this is needed.  Empty Subscribe
    // const visitPage = `${this.assessmentUrl}/lookup/domains/visit_page`;
    // this.http.get(visitPage).subscribe();
  }
}



// WEBPACK FOOTER //
// ./src/providers/mobile-offline-data/mobile-offline-data.ts